import { useMemo, useState } from 'react'
import { Badge, BadgeStatus, Button, Card, ConfirmDialog, DataTable, PageHeader, Tabs, type KolomTabel } from '@/components'
import { api, pesanKesalahan } from '@/lib/api'
import { formatRupiah, formatTanggal } from '@/lib/format'
import { useApi, useAksi } from '@/lib/useApi'
import { useAuth } from '@/lib/auth'

interface ButirPersetujuan {
  id: string
  jenis: string
  nomor: string
  judul: string
  unit?: string
  jumlah: number
  tanggal?: string
  status: string
  dapatSetujui: boolean
  dapatTolak: boolean
}

/**
 * Daftar persetujuan yang memerlukan tindakan.
 *
 * Backend belum menyediakan endpoint /approvals tunggal, jadi daftar ini
 * dihimpun dari endpoint dokumen yang memang ada pada kontrak API:
 *   /budgets (status SUBMITTED)          -> POST /budgets/:id/approve
 *   /petty-cash (status SUBMITTED)       -> POST /petty-cash/:id/approve|reject
 *   /accountability (status SUBMITTED)   -> POST /accountability/:id/approve
 *   /interunit-transfers (REQUESTED)     -> POST /interunit-transfers/:id/approve
 *   /transactions (status SUBMITTED)     -> POST /transactions/:id/approve
 */
export default function PersetujuanPage() {
  const { punyaIzin } = useAuth()
  const aksi = useAksi()
  const [tabAktif, setTabAktif] = useState('semua')
  const [konfirmasi, setKonfirmasi] = useState<{ butir: ButirPersetujuan; tolak: boolean } | null>(null)

  const anggaran = useApi<unknown>('/budgets', { status: 'SUBMITTED', perPage: 100 })
  const kasKecil = useApi<unknown>('/petty-cash', { status: 'SUBMITTED', perPage: 100 })
  const accountability = useApi<unknown>('/accountability', { status: 'SUBMITTED', perPage: 100 })
  const transfer = useApi<unknown>('/interunit-transfers', { status: 'REQUESTED', perPage: 100 })
  const transaksi = useApi<unknown>('/transactions', { status: 'SUBMITTED', perPage: 100 })

  const butir = useMemo<ButirPersetujuan[]>(() => {
    const hasil: ButirPersetujuan[] = []

    for (const b of daftar(anggaran.data)) {
      hasil.push({
        id: b.id,
        jenis: 'Anggaran',
        nomor: teks(b, ['budgetCode']),
        judul: teks(b, ['name']),
        unit: teksNama(b.unit),
        jumlah: angka(b, ['totalFinal', 'totalPlanned']),
        tanggal: teksOpsional(b, ['submittedAt', 'createdAt']),
        status: teks(b, ['status']),
        dapatSetujui: punyaIzin('budget:approve'),
        dapatTolak: false,
      })
    }
    for (const b of daftar(kasKecil.data)) {
      hasil.push({
        id: b.id,
        jenis: 'Kas Kecil',
        nomor: teks(b, ['requestNumber']),
        judul: teks(b, ['purpose']),
        unit: teksNama(b.unit),
        jumlah: angka(b, ['requestedAmount']),
        tanggal: teksOpsional(b, ['requestDate']),
        status: teks(b, ['status']),
        dapatSetujui: punyaIzin('pettycash:approve'),
        dapatTolak: punyaIzin('pettycash:approve', 'pettycash:review'),
      })
    }
    for (const b of daftar(accountability.data)) {
      hasil.push({
        id: b.id,
        jenis: 'Pertanggungjawaban',
        nomor: teks(b, ['reportNumber']),
        judul: `Laporan periode ke-${angka(b, ['periodNumber'])}`,
        unit: teksNama(b.unit),
        jumlah: angka(b, ['totalSpent']),
        tanggal: teksOpsional(b, ['submittedAt', 'createdAt']),
        status: teks(b, ['status']),
        dapatSetujui: punyaIzin('accountability:approve'),
        dapatTolak: false,
      })
    }
    for (const b of daftar(transfer.data)) {
      hasil.push({
        id: b.id,
        jenis: 'Transfer Antarunit',
        nomor: teks(b, ['transferNumber']),
        judul: teks(b, ['purpose']) || 'Transfer antarunit',
        unit: teksNama(b.sourceUnit),
        jumlah: angka(b, ['amount']),
        tanggal: teksOpsional(b, ['transferDate']),
        status: teks(b, ['status']),
        dapatSetujui: punyaIzin('transfer:approve'),
        dapatTolak: false,
      })
    }
    for (const b of daftar(transaksi.data)) {
      hasil.push({
        id: b.id,
        jenis: 'Transaksi',
        nomor: teks(b, ['transactionNumber']),
        judul: teks(b, ['description']),
        unit: teksNama(b.unit),
        jumlah: angka(b, ['amount']),
        tanggal: teksOpsional(b, ['transactionDate']),
        status: teks(b, ['status']),
        dapatSetujui: punyaIzin('transaction:approve'),
        dapatTolak: false,
      })
    }

    return hasil
  }, [anggaran.data, kasKecil.data, accountability.data, transfer.data, transaksi.data, punyaIzin])

  const memuat =
    anggaran.memuat || kasKecil.memuat || accountability.memuat || transfer.memuat || transaksi.memuat
  const galatPertama =
    anggaran.galat ?? kasKecil.galat ?? accountability.galat ?? transfer.galat ?? transaksi.galat

  const jenis = Array.from(new Set(butir.map((b) => b.jenis)))
  const butirTampil = tabAktif === 'semua' ? butir : butir.filter((b) => b.jenis === tabAktif)

  const muatSemua = () => {
    anggaran.muat()
    kasKecil.muat()
    accountability.muat()
    transfer.muat()
    transaksi.muat()
  }

  const ruteUntuk = (b: ButirPersetujuan, tolak: boolean): string => {
    switch (b.jenis) {
      case 'Anggaran':
        return `/budgets/${b.id}/approve`
      case 'Kas Kecil':
        return `/petty-cash/${b.id}/${tolak ? 'reject' : 'approve'}`
      case 'Pertanggungjawaban':
        return `/accountability/${b.id}/approve`
      case 'Transfer Antarunit':
        return `/interunit-transfers/${b.id}/approve`
      default:
        return `/transactions/${b.id}/approve`
    }
  }

  const kolom: KolomTabel<ButirPersetujuan>[] = [
    { kunci: 'jenis', judul: 'Jenis Dokumen', render: (b) => <Badge nada="info">{b.jenis}</Badge> },
    { kunci: 'nomor', judul: 'Nomor', render: (b) => <span className="font-medium text-netral-800">{b.nomor}</span> },
    { kunci: 'judul', judul: 'Ringkasan' },
    { kunci: 'unit', judul: 'Unit', render: (b) => b.unit ?? '—' },
    { kunci: 'tanggal', judul: 'Tanggal', render: (b) => (b.tanggal ? formatTanggal(b.tanggal) : '—') },
    { kunci: 'jumlah', judul: 'Nilai', rata: 'kanan', render: (b) => formatRupiah(b.jumlah) },
    { kunci: 'status', judul: 'Status', render: (b) => <BadgeStatus status={b.status} /> },
    {
      kunci: 'aksi',
      judul: 'Tindakan',
      sortable: false,
      render: (b) => (
        <div className="flex flex-wrap gap-1.5">
          {b.dapatSetujui ? (
            <Button ukuran="kecil" onClick={() => setKonfirmasi({ butir: b, tolak: false })}>
              Setujui
            </Button>
          ) : null}
          {b.dapatTolak ? (
            <Button ukuran="kecil" varian="bahaya" onClick={() => setKonfirmasi({ butir: b, tolak: true })}>
              Tolak
            </Button>
          ) : null}
          {!b.dapatSetujui && !b.dapatTolak ? (
            <span className="text-xs text-netral-400">Tanpa hak aksi</span>
          ) : null}
        </div>
      ),
    },
  ]

  return (
    <div>
      <PageHeader
        judul="Persetujuan"
        deskripsi="Dokumen yang menunggu tindakan Anda. Setiap tindakan dicatat pada audit log."
        aksi={<Button varian="garis" onClick={muatSemua}>Muat ulang</Button>}
      />

      {galatPertama && butir.length === 0 ? (
        <Card>
          <p className="text-sm text-red-700">
            Sebagian endpoint persetujuan belum dapat diakses: {galatPertama}
          </p>
        </Card>
      ) : null}

      <Tabs
        tab={[{ id: 'semua', label: 'Semua', jumlah: butir.length }, ...jenis.map((j) => ({ id: j, label: j, jumlah: butir.filter((b) => b.jenis === j).length }))]}
        aktif={tabAktif}
        onUbah={setTabAktif}
      />

      <DataTable
        kolom={kolom}
        data={butirTampil}
        kunciBaris={(b) => `${b.jenis}-${b.id}`}
        memuat={memuat}
        pesanKosong="Tidak ada dokumen yang menunggu persetujuan Anda."
        deskripsiKosong="Semua pengajuan pada unit Anda sudah ditindaklanjuti."
      />

      <ConfirmDialog
        terbuka={konfirmasi !== null}
        onTutup={() => setKonfirmasi(null)}
        judul={konfirmasi?.tolak ? 'Tolak Pengajuan' : 'Setujui Pengajuan'}
        pesan={
          <>
            {konfirmasi?.tolak ? 'Menolak' : 'Menyetujui'} <strong>{konfirmasi?.butir.nomor}</strong> (
            {konfirmasi?.butir.jenis}) sebesar <strong>{formatRupiah(konfirmasi?.butir.jumlah ?? 0)}</strong>.
            {konfirmasi?.tolak ? ' Alasan penolakan wajib diisi.' : ''}
          </>
        }
        labelKonfirmasi={konfirmasi?.tolak ? 'Tolak' : 'Setujui'}
        berbahaya={konfirmasi?.tolak ?? false}
        labelAlasan={konfirmasi?.tolak ? 'Alasan penolakan' : undefined}
        minimalAlasan={5}
        memuat={aksi.memuat}
        onKonfirmasi={async (alasan) => {
          if (!konfirmasi) return
          const { butir: b, tolak } = konfirmasi
          const rute = ruteUntuk(b, tolak)
          const hasil = await aksi.jalankan(
            () => api.post(rute, tolak ? { reason: alasan } : {}),
            { sukses: `${b.jenis} ${b.nomor} ${tolak ? 'ditolak' : 'disetujui'}.` },
          )
          if (hasil !== null) {
            setKonfirmasi(null)
            muatSemua()
          }
        }}
      />
    </div>
  )
}

function daftar(data: unknown): Array<Record<string, unknown> & { id: string }> {
  const arr = Array.isArray(data)
    ? data
    : Array.isArray((data as { items?: unknown[] } | null)?.items)
      ? ((data as { items: unknown[] }).items)
      : []
  return arr.filter((x): x is Record<string, unknown> & { id: string } => {
    return typeof x === 'object' && x !== null && typeof (x as { id?: unknown }).id === 'string'
  })
}

function teks(b: Record<string, unknown>, kandidat: string[]): string {
  for (const k of kandidat) {
    const v = b[k]
    if (typeof v === 'string' && v.trim() !== '') return v
  }
  return '-'
}

function teksOpsional(b: Record<string, unknown>, kandidat: string[]): string | undefined {
  for (const k of kandidat) {
    const v = b[k]
    if (typeof v === 'string' && v.trim() !== '') return v
  }
  return undefined
}

function teksNama(v: unknown): string | undefined {
  if (v === null || v === undefined) return undefined
  if (typeof v === 'string') return v
  if (typeof v === 'object') {
    const o = v as Record<string, unknown>
    if (typeof o.name === 'string') return o.name
  }
  return undefined
}

function angka(b: Record<string, unknown>, kandidat: string[]): number {
  for (const k of kandidat) {
    const v = b[k]
    if (typeof v === 'number' && Number.isFinite(v)) return v
    if (typeof v === 'string') {
      const n = Number(v.replace(/[^0-9.-]/g, ''))
      if (Number.isFinite(n)) return n
    }
  }
  return 0
}

export function pesanKesalahanHalaman(e: unknown): string {
  return pesanKesalahan(e)
}
